// Auth - CAIXA MESTRE (Local + Online Mode)
// Modo local com sincronização opcional com Supabase

import { PERFIS } from '../utils/permissions.js'
import { isSupabaseAvailable, supabase } from '../data/supabase.js'

// ========== TIPOS DE USUÁRIO ==========
export const USER_TYPES = PERFIS

// ========== TRADUÇÃO DE MENSAGENS DO SUPABASE ==========
function traduzirErro(msg = '') {
  if (msg.includes('Invalid login credentials'))      return 'Usuário ou senha incorretos.'
  if (msg.includes('Email not confirmed'))            return 'Email não confirmado. Verifique sua caixa de entrada.'
  if (msg.includes('User already registered'))        return 'Este email já está cadastrado.'
  if (msg.includes('Password should be at least'))    return 'A senha deve ter pelo menos 6 caracteres.'
  if (msg.includes('Unable to validate email'))       return 'Email inválido.'
  if (msg.includes('Email rate limit exceeded'))      return 'Muitas tentativas. Aguarde alguns minutos.'
  if (msg.includes('Too Many Requests') || msg.includes('rate limit')) return 'Muitas tentativas. Aguarde alguns minutos e tente novamente.'
  if (msg.includes('User not found'))                 return 'Usuário não encontrado.'
  if (msg.includes('Invalid email'))                  return 'Email inválido.'
  if (msg.includes('Signup is disabled'))             return 'Cadastro desativado. Entre em contato com o suporte.'
  if (msg.includes('Email link is invalid'))          return 'Link de confirmação inválido ou expirado.'
  if (msg.includes('Token has expired'))              return 'Link expirado. Solicite um novo email de confirmação.'
  if (msg.includes('Network'))                        return 'Erro de conexão. Verifique sua internet.'
  return msg
}

// ========== SESSÃO ==========
export function getSession() {
  const saved = localStorage.getItem('caixa_mestre_session')
  return saved ? JSON.parse(saved) : null
}

export function setSession(session) {
  localStorage.setItem('caixa_mestre_session', JSON.stringify(session))
}

// ========== LOGIN ==========
export async function login(credentials) {
  console.log('[auth.js] LOGIN - login:', credentials.login)

  const email = credentials.login.includes('@')
    ? credentials.login
    : `${credentials.login}@caixa-mestre.local`

  // 1. Autenticar via Supabase Auth (gera JWT com empresa_id via hook)
  const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
    email,
    password: credentials.senha
  })

  if (authError) {
    // Fallback: admin padrão local (willian) — sem empresa, acesso direto ao painel
    if (checkLocalAdmin(credentials.login, credentials.senha)) {
      const session = {
        usuarioId: DEFAULT_USER.id,
        usuarioNome: DEFAULT_USER.nome,
        login: DEFAULT_USER.login,
        perfil: DEFAULT_USER.perfil,
        tipoUsuario: DEFAULT_USER.perfil,
        empresaId: null,
        empresaNome: null,
        isSuperAdmin: true,
        criadoEm: Date.now()
      }
      console.log('[auth.js] Login local (fallback) super admin:', DEFAULT_USER.nome)
      setSession(session)
      return session
    }
    throw new Error(traduzirErro(authError.message))
  }

  const authUser = authData.user

  // 2. Buscar dados do usuário na tabela usando service role via RPC (bypass RLS no bootstrap)
  const { data: userData, error: userError } = await supabase
    .from('usuarios')
    .select('*, empresas(id, nome, ativo)')
    .eq('id', authUser.id)
    .maybeSingle()

  if (userError || !userData) {
    // Usuário autenticado mas sem registro em usuarios (ex: empresa excluída + recadastro)
    // Criar empresa e usuário automaticamente
    try {
      const nomeEmpresa = authUser.user_metadata?.nomeEmpresa || 'Minha Empresa'
      const nomeAdmin = authUser.user_metadata?.nome || authUser.email

      const { data: rpcData, error: rpcError } = await supabase.rpc('bootstrap_empresa', {
        p_user_id: authUser.id,
        p_email: authUser.email,
        p_nome: nomeAdmin,
        p_nome_empresa: nomeEmpresa
      })

      if (rpcError) throw rpcError

      const empresaId = rpcData?.empresa_id || rpcData?.empresaId
      const session = {
        usuarioId: authUser.id, usuarioNome: nomeAdmin, login: authUser.email,
        perfil: 'admin', tipoUsuario: 'admin',
        empresaId, empresaNome: nomeEmpresa, isSuperAdmin: false, criadoEm: Date.now()
      }
      setSession(session)
      return session
    } catch (_e) {
      console.error('[auth.js] Erro no bootstrap_empresa:', _e)
      await supabase.auth.signOut()
      throw new Error('Usuário não encontrado ou inativo.')
    }
  }

  if (userData.empresas && userData.empresas.ativo === false) {
    await supabase.auth.signOut()
    throw new Error('Esta empresa está suspensa. Entre em contato com o suporte.')
  }

  const isSuperAdmin = credentials.login === DEFAULT_USER.login || userData.is_super_admin === true

  const session = {
    usuarioId: userData.id,
    usuarioNome: userData.nome || credentials.login,
    login: userData.login || credentials.login,
    perfil: userData.perfil || PERFIS.almoxarife,
    tipoUsuario: userData.perfil || PERFIS.almoxarife,
    empresaId: userData.empresa_id || null,
    empresaNome: userData.empresas?.nome || null,
    isSuperAdmin,
    criadoEm: Date.now()
  }

  console.log('[auth.js] Login bem-sucedido:', session.usuarioNome, '| Empresa:', session.empresaNome)
  setSession(session)
  return session
}

export async function logout() {
  localStorage.removeItem('caixa_mestre_session')
  try { await supabase.auth.signOut() } catch (e) { /* ignora */ }
  return null
}

// ========== GERENCIAMENTO DE USUÁRIOS ==========
// Supabase é a fonte de verdade. localStorage guarda apenas cache da sessão de login.

const DEFAULT_USER = {
  id: 'edc34b32-d80d-46d9-853b-a2fd942bc956', // UUID real do Supabase Auth
  nome: 'Willian Costa',
  login: 'willian.costa7465@gmail.com',
  perfil: PERFIS.admin,
  ativo: true,
  criadoEm: 0
}

// Senha do admin local protegida (não expor em código-fonte)
function checkLocalAdmin(login, senha) {
  if (login !== DEFAULT_USER.login) return false
  // Hash simples para não expor senha em plaintext
  let h = 0
  const s = String(senha)
  for (let i = 0; i < s.length; i++) { h = ((h << 5) - h) + s.charCodeAt(i); h |= 0 }
  return h === 1482120371 // hash da senha correta
}

function remoteToLocal(r) {
  return {
    id: r.id,
    nome: r.nome || r.login || '(Sem nome)',
    login: r.login || r.email || '',
    senha: r.senha_hash || '',
    perfil: r.perfil || PERFIS.almoxarife,
    ativo: r.ativo !== false,
    criadoEm: r.criado_em || 0,
    empresaId: r.empresa_id || r.empresas?.id || null,
    empresaNome: r.empresas?.nome || null
  }
}

export function getUsers() {
  return [DEFAULT_USER]
}

export function saveUsers() {}

export async function createUser(userData) {
  if (!isSupabaseAvailable()) throw new Error('Sem conexão com o servidor.')

  const session = getSession()
  const empresaId = userData.empresaId || session?.empresaId || 'a1b2c3d4-0000-0000-0000-000000000001'

  const email = userData.login.includes('@') ? userData.login : `${userData.login}@caixa-mestre.local`
  const senha = String(userData.senha || '123456')

  // 1. Criar no Supabase Auth (signUp) — não exige confirmação se estiver desativada no projeto
  let authId = null
  try {
    const { data: signData, error: signError } = await supabase.auth.signUp({ email, password: senha })
    if (!signError && signData?.user?.id) {
      authId = signData.user.id
      console.log('[auth.js] Usuário criado no Supabase Auth:', authId)
    } else if (signError) {
      console.warn('[auth.js] Falha no signUp Auth (continuando):', signError.message)
    }
  } catch (e) {
    console.warn('[auth.js] Exceção no signUp Auth (continuando):', e?.message)
  }

  const id = authId || (crypto.randomUUID ? crypto.randomUUID() : Date.now().toString())
  const now = Date.now()

  // 2. Salvar na tabela usuarios (incluindo senha_hash e empresa_id)
  const { error } = await supabase.from('usuarios').upsert({
    id,
    login: userData.login,
    nome: userData.nome,
    email,
    senha_hash: senha,
    perfil: userData.perfil || PERFIS.almoxarife,
    ativo: true,
    empresa_id: empresaId,
    criado_em: now,
    atualizado_em: now
  }, { onConflict: 'id' })

  if (error) throw new Error('Erro ao salvar usuário: ' + error.message)

  console.log('[auth.js] Usuário criado e sincronizado:', userData.login)
  return id
}

export async function listUsers() {
  if (!isSupabaseAvailable()) {
    console.warn('[auth.js] Supabase indisponível, retornando usuário padrão')
    return [DEFAULT_USER]
  }
  const session = getSession()
  const empresaId = session?.empresaId
  let query = supabase.from('usuarios').select('*').order('nome', { ascending: true })
  if (empresaId) query = query.eq('empresa_id', empresaId)
  const { data, error } = await query
  if (error) {
    console.warn('[auth.js] Erro ao listar usuários do Supabase:', error.message)
    return []
  }
  const SUPER_ADMIN_EMAIL = 'willian.costa7465@gmail.com'
  const list = (data || [])
    .filter(u => u.email !== SUPER_ADMIN_EMAIL && u.login !== SUPER_ADMIN_EMAIL)
    .map(remoteToLocal)
  return list
}

export async function updateUser(id, updates) {
  if (!isSupabaseAvailable()) throw new Error('Sem conexão com o servidor.')
  const payload = { atualizado_em: Date.now() }
  if (updates.nome !== undefined) payload.nome = updates.nome
  if (updates.perfil !== undefined) payload.perfil = updates.perfil
  if (updates.ativo !== undefined) payload.ativo = updates.ativo
  if (updates.senha !== undefined && updates.senha !== '') payload.senha_hash = String(updates.senha)
  const { error } = await supabase.from('usuarios').update(payload).eq('id', id)
  if (error) throw new Error('Erro ao atualizar usuário: ' + error.message)
}

export async function disableUser(id) {
  return updateUser(id, { ativo: false })
}

export async function enableUser(id) {
  return updateUser(id, { ativo: true })
}

export async function deleteUser(id) {
  if (!isSupabaseAvailable()) throw new Error('Sem conexão com o servidor.')
  const { error } = await supabase.from('usuarios').delete().eq('id', id)
  if (error) throw new Error('Erro ao excluir usuário: ' + error.message)
}

// ========== UTILITÁRIOS ==========
export function onAuthStateChange(callback) {
  // Usar listener do Supabase Auth para mudanças de sessão
  if (isSupabaseAvailable()) {
    const { data } = supabase.auth.onAuthStateChange((event, supaSession) => {
      // Se há sessão Supabase, usa ela
      if (supaSession) {
        callback(getSession())
        return
      }
      // Se não há sessão Supabase, mas há sessão local (super admin fallback), mantém a local
      const localSession = getSession()
      if (localSession?.isSuperAdmin) {
        console.log('[auth.js] onAuthStateChange: Mantendo sessão local de super admin (sem JWT Supabase)')
        callback(localSession)
        return
      }
      // Caso contrário, sem sessão
      callback(null)
    })
    return data.subscription?.unsubscribe || (() => {})
  }
  return () => {}
}

export function isAuthenticated() {
  return getSession() !== null
}

export function hasPermission(perfil, permission) {
  return true
}

// ========== PERGUNTA DE SEGURANÇA ==========
const SECURITY_STORAGE_KEY = 'caixa_mestre_security'

export function setSecurityQuestion(login, question, answer) {
  const saved = localStorage.getItem(SECURITY_STORAGE_KEY)
  const security = saved ? JSON.parse(saved) : {}
  
  security[login] = {
    question,
    answer: answer.toLowerCase().trim(),
    updatedAt: Date.now()
  }
  
  localStorage.setItem(SECURITY_STORAGE_KEY, JSON.stringify(security))
}

export function getSecurityQuestion(login) {
  const saved = localStorage.getItem(SECURITY_STORAGE_KEY)
  const security = saved ? JSON.parse(saved) : {}
  
  const userSecurity = security[login]
  if (!userSecurity) {
    return null
  }
  
  return userSecurity.question
}

export function verifySecurityAnswer(login, answer) {
  const saved = localStorage.getItem(SECURITY_STORAGE_KEY)
  const security = saved ? JSON.parse(saved) : {}
  
  const userSecurity = security[login]
  if (!userSecurity) {
    return false
  }
  
  return userSecurity.answer === answer.toLowerCase().trim()
}

export function resetPasswordWithSecurityQuestion(login, answer, newPassword) {
  // Verificar resposta
  if (!verifySecurityAnswer(login, answer)) {
    throw new Error('Resposta incorreta')
  }
  
  // No modo local, apenas retornar sucesso
  // (não há senha real para resetar, o login é direto)
  return { success: true, message: 'Senha redefinida com sucesso' }
}
